import React, { useMemo, useState } from 'react';
import {
  ClientLifecycleStatus,
  datetimeLocalToIso,
  isoToDatetimeLocalValue,
  LeadLifecycleStatus,
  ProspectLifecycleStatus,
} from '../../services/crmService';
import { sanitizeWebsiteUrlForSave } from '../../utils/websiteUrl';

type ContactFormMode = 'lead' | 'prospect' | 'client';

export interface ContactFormValues {
  title?: string;
  full_name: string;
  email: string;
  phone: string;
  company_name: string;
  website_url: string;
  job_title?: string;
  source: string;
  address: string;
  notes: string;
  stage?: 'lead' | 'prospect' | 'client' | 'churned';
  lifecycle_status?: LeadLifecycleStatus | ProspectLifecycleStatus | ClientLifecycleStatus;
  tags?: string[];
  estimated_deal_value?: string | null;
  next_follow_up_at?: string | null;
}

interface ContactFormModalProps {
  title: string;
  submitLabel: string;
  mode: ContactFormMode;
  values: ContactFormValues;
  onClose: () => void;
  onSubmit: (values: ContactFormValues) => void;
  /** Save failure from the API. Rendered inside the modal — the page-level banner
   *  sits behind the backdrop, which made failed saves look like dead buttons. */
  error?: string;
  submitting?: boolean;
}

/** Channel list the dashboard's source breakdown buckets; keep the two aligned. */
const SOURCE_OPTIONS = [
  'Direct',
  'Door-to-door',
  'Website',
  'Flyer',
  'Referral',
  'Cold call',
  'Social media',
  'Email',
  'Chatbot',
  'Photo capture',
  'Import',
  'Manual input',
  'Other',
];

const stageOptionsByMode: Record<ContactFormMode, Array<'lead' | 'prospect' | 'client' | 'churned'>> = {
  lead: ['lead', 'prospect'],
  prospect: ['prospect', 'client'],
  client: ['client', 'churned'],
};

const lifecycleOptionsByMode: Record<
  ContactFormMode,
  Array<LeadLifecycleStatus | ProspectLifecycleStatus | ClientLifecycleStatus>
> = {
  lead: ['active', 'dormant', 'archived', 'do_not_contact'],
  prospect: ['active', 'nurturing', 'dormant', 'lost'],
  client: ['active', 'paused', 'former'],
};

const ContactFormModal: React.FC<ContactFormModalProps> = ({
  title,
  submitLabel,
  mode,
  values,
  onClose,
  onSubmit,
  error,
  submitting = false,
}) => {
  const [formData, setFormData] = useState<ContactFormValues>(values);
  const [tagsCsv, setTagsCsv] = useState((values.tags || []).join(', '));
  // Seed from the contact's existing follow-up: starting blank and always
  // submitting the parsed value silently cleared the date on every save.
  const [nextFollowUpLocal, setNextFollowUpLocal] = useState(() =>
    isoToDatetimeLocalValue(values.next_follow_up_at)
  );

  // A lead may carry a source we don't list (a custom import value, or one added
  // later). Show it rather than silently snapping the select to 'Direct'.
  const sourceOptions = useMemo(() => {
    const current = (formData.source || '').trim();
    return current && !SOURCE_OPTIONS.includes(current) ? [current, ...SOURCE_OPTIONS] : SOURCE_OPTIONS;
  }, [formData.source]);

  const stageOptions = stageOptionsByMode[mode];
  const lifecycleOptions = lifecycleOptionsByMode[mode];
  const showExtendedFields = mode !== 'lead' || submitLabel.toLowerCase().includes('save');

  // Leads are often captured fast with partial info: only a name OR company is
  // required, and email is optional. Prospects/clients still require name + email.
  const isLead = mode === 'lead';
  const canSubmit = useMemo(() => {
    if (isLead) return Boolean(formData.full_name.trim() || formData.company_name.trim());
    return Boolean(formData.full_name.trim() && formData.email.trim());
  }, [isLead, formData.company_name, formData.email, formData.full_name]);

  const updateField = <K extends keyof ContactFormValues>(field: K, value: ContactFormValues[K]) => {
    setFormData((prev) => ({ ...prev, [field]: value }));
  };

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    onSubmit({
      ...formData,
      website_url: sanitizeWebsiteUrlForSave(formData.website_url || ''),
      source: formData.source || 'Direct',
      tags: tagsCsv
        .split(',')
        .map((tag) => tag.trim())
        .filter(Boolean),
      next_follow_up_at: datetimeLocalToIso(nextFollowUpLocal),
    });
  };

  const sourceSelect = (
    <select
      value={formData.source}
      onChange={(e) => updateField('source', e.target.value)}
      className="w-full px-3 py-3 border border-border rounded-xl bg-input text-fg"
    >
      {sourceOptions.map((option) => (
        <option key={option} value={option}>
          {option}
        </option>
      ))}
    </select>
  );

  return (
    <div className="modal-backdrop">
      <div className="relative top-10 mx-auto p-5 border border-border w-full max-w-2xl shadow-lg rounded-2xl bg-surface">
        <div className="mt-3">
          <div className="flex justify-between items-center mb-6">
            <h3 className="text-lg font-semibold text-fg">{title}</h3>
            <button onClick={onClose} className="text-fg-muted hover:text-fg-secondary" type="button">
              <svg className="h-6 w-6" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
              </svg>
            </button>
          </div>

          <form onSubmit={handleSubmit} className="space-y-4">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium text-fg-secondary mb-2">
                  {isLead ? 'Full Name' : 'Full Name *'}
                </label>
                <div className="flex gap-2">
                  <input
                    type="text"
                    list="contact-title-options"
                    aria-label="Title"
                    placeholder="Title"
                    value={formData.title ?? ''}
                    onChange={(e) => updateField('title', e.target.value)}
                    className="w-24 px-3 py-3 border border-border rounded-xl bg-input text-fg"
                  />
                  <datalist id="contact-title-options">
                    <option value="Dr." />
                    <option value="Mr." />
                    <option value="Mrs." />
                    <option value="Ms." />
                    <option value="Prof." />
                  </datalist>
                  <input
                    type="text"
                    required={!isLead}
                    placeholder="First and last name"
                    value={formData.full_name}
                    onChange={(e) => updateField('full_name', e.target.value)}
                    className="flex-1 px-3 py-3 border border-border rounded-xl bg-input text-fg"
                  />
                </div>
              </div>
              <div>
                <label className="block text-sm font-medium text-fg-secondary mb-2">
                  {isLead ? 'Email' : 'Email *'}
                </label>
                <input
                  type="email"
                  required={!isLead}
                  placeholder={isLead ? 'name@company.com (optional)' : 'name@company.com'}
                  value={formData.email}
                  onChange={(e) => updateField('email', e.target.value)}
                  className="w-full px-3 py-3 border border-border rounded-xl bg-input text-fg"
                />
              </div>
            </div>
            {isLead ? (
              <p className="-mt-2 text-xs text-fg-muted">
                Add a name or company to save — every other field is optional.
              </p>
            ) : null}

            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium text-fg-secondary mb-2">Phone</label>
                <input
                  type="tel"
                  placeholder="(555) 123-4567"
                  value={formData.phone}
                  onChange={(e) => updateField('phone', e.target.value)}
                  className="w-full px-3 py-3 border border-border rounded-xl bg-input text-fg"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-fg-secondary mb-2">Company</label>
                <input
                  type="text"
                  placeholder="Company name"
                  value={formData.company_name}
                  onChange={(e) => updateField('company_name', e.target.value)}
                  className="w-full px-3 py-3 border border-border rounded-xl bg-input text-fg"
                />
              </div>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium text-fg-secondary mb-2">Website URL</label>
                {/* Deliberately not type="url": the app stores bare domains
                    ("example.com"), which native URL validation rejects — the form
                    then refused to submit with no visible reason. */}
                <input
                  type="text"
                  inputMode="url"
                  value={formData.website_url}
                  onChange={(e) => updateField('website_url', e.target.value)}
                  placeholder="example.com"
                  className="w-full px-3 py-3 border border-border rounded-xl bg-input text-fg"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-fg-secondary mb-2">Job Title</label>
                <input
                  type="text"
                  value={formData.job_title || ''}
                  onChange={(e) => updateField('job_title', e.target.value)}
                  className="w-full px-3 py-3 border border-border rounded-xl bg-input text-fg"
                />
              </div>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium text-fg-secondary mb-2">Source</label>
                {sourceSelect}
              </div>
              <div>
                <label className="block text-sm font-medium text-fg-secondary mb-2">Lifecycle Status</label>
                <select
                  value={formData.lifecycle_status || lifecycleOptions[0]}
                  onChange={(e) =>
                    updateField('lifecycle_status', e.target.value as ContactFormValues['lifecycle_status'])
                  }
                  className="w-full px-3 py-3 border border-border rounded-xl bg-input text-fg"
                >
                  {lifecycleOptions.map((option) => (
                    <option key={option} value={option}>
                      {option.replaceAll('_', ' ')}
                    </option>
                  ))}
                </select>
              </div>
            </div>

            {showExtendedFields ? (
              <>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-fg-secondary mb-2">Stage</label>
                    <select
                      value={formData.stage || stageOptions[0]}
                      onChange={(e) => updateField('stage', e.target.value as ContactFormValues['stage'])}
                      className="w-full px-3 py-3 border border-border rounded-xl bg-input text-fg"
                    >
                      {stageOptions.map((option) => (
                        <option key={option} value={option}>
                          {option}
                        </option>
                      ))}
                    </select>
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-fg-secondary mb-2">Estimated Deal Value</label>
                    <input
                      type="number"
                      min="0"
                      step="0.01"
                      value={formData.estimated_deal_value ?? ''}
                      onChange={(e) => updateField('estimated_deal_value', e.target.value)}
                      className="w-full px-3 py-3 border border-border rounded-xl bg-input text-fg"
                    />
                  </div>
                </div>
                <div>
                  <label className="block text-sm font-medium text-fg-secondary mb-2">Next Follow-up</label>
                  <input
                    type="datetime-local"
                    value={nextFollowUpLocal}
                    onChange={(e) => setNextFollowUpLocal(e.target.value)}
                    className="w-full px-3 py-3 border border-border rounded-xl bg-input text-fg"
                  />
                </div>
              </>
            ) : null}

            <div>
              <label className="block text-sm font-medium text-fg-secondary mb-2">Address</label>
              <textarea
                rows={2}
                value={formData.address}
                onChange={(e) => updateField('address', e.target.value)}
                className="w-full px-3 py-3 border border-border rounded-xl bg-input text-fg"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-fg-secondary mb-2">Notes</label>
              <textarea
                rows={3}
                value={formData.notes}
                onChange={(e) => updateField('notes', e.target.value)}
                className="w-full px-3 py-3 border border-border rounded-xl bg-input text-fg"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-fg-secondary mb-2">Tags (comma-separated)</label>
              <input
                type="text"
                value={tagsCsv}
                onChange={(e) => setTagsCsv(e.target.value)}
                className="w-full px-3 py-3 border border-border rounded-xl bg-input text-fg"
              />
            </div>

            {error ? (
              <p role="alert" className="rounded-xl border border-danger/40 bg-danger/10 px-3 py-2 text-sm text-danger-fg">
                {error}
              </p>
            ) : null}

            <div className="flex justify-end space-x-3 pt-4">
              <button type="button" onClick={onClose} className="px-4 py-2 btn-surface-muted">
                Cancel
              </button>
              <button
                type="submit"
                disabled={!canSubmit || submitting}
                className="px-4 py-2 text-sm font-medium text-primary-fg bg-gradient-to-r from-primary to-primary-dark hover:from-primary-dark hover:to-primary rounded-xl disabled:opacity-50"
              >
                {submitting ? 'Saving…' : submitLabel}
              </button>
            </div>
          </form>
        </div>
      </div>
    </div>
  );
};

export default ContactFormModal;
